import { Dialog, IconButton } from '@mui/material';
import { CloseIcon } from '@truvhq/ui';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { useOnboardingState } from 'entities/onboarding/api/useOnboardingState';
import { hasAppAccess } from 'entities/user/lib/hasAppAccess';
import { useAutoOpenOnboarding } from 'features/onboarding/lib/useAutoOpenOnboarding';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { OnboardingConversation } from 'features/onboarding/ui/OnboardingConversation';
import { useAuth } from 'shared/lib/auth/useAuth';
import { FeatureFlag } from 'shared/lib/features/common';

// Mounts the conversational onboarding as a global popup driven by useOnboardingDialogStore.
// Opening it automatically the first time is useAutoOpenOnboarding's job; after that the user opens
// it manually via the launcher.
//
// This is mounted in App.tsx outside AuthGuard, so it has to check app access itself — otherwise it
// renders over the "Account pending" screen and calls endpoints that answer 403 for those users.
export function OnboardingDialog() {
  const { user } = useAuth();
  const assistantEnabled = useFeatureFlagEnabled(FeatureFlag.AI_ASSISTANT);
  const canUseAssistant = hasAppAccess(user) && Boolean(assistantEnabled);
  const isOpen = useOnboardingDialogStore((state) => state.isOpen);
  const sessionKey = useOnboardingDialogStore((state) => state.sessionKey);
  const close = useOnboardingDialogStore((state) => state.close);

  // Render mode comes from constance (via the onboarding state endpoint): "docked" is a right-side
  // panel that leaves the dashboard interactive; "window" is the centered dialog. Preload it (not
  // gated on isOpen) so the layout is known before the dialog opens — otherwise a docked chat would
  // briefly flash the window mode while the request resolved.
  const { data: onboardingState, isLoading: isLayoutLoading } = useOnboardingState({ enabled: canUseAssistant });
  const isDocked = onboardingState?.layout === 'docked';

  useAutoOpenOnboarding({ enabled: canUseAssistant, onboardingState });

  const closeButton = (
    <IconButton
      aria-label="Close onboarding"
      onClick={close}
      sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: 'text.secondary' }}
    >
      <CloseIcon />
    </IconButton>
  );
  const conversation = (
    <div className="h-full overflow-hidden">
      <OnboardingConversation key={sessionKey} />
    </div>
  );

  if (!canUseAssistant) {
    return null;
  }

  // Hold off rendering a container until the layout is resolved, so a docked chat never flashes the
  // window mode first. Preloaded above, so this is settled before the user can open the dialog.
  if (isLayoutLoading) {
    return null;
  }

  if (isDocked) {
    return (
      isOpen && (
        <div
          role="dialog"
          aria-label="Get started"
          className="fixed inset-y-0 right-0 z-[1300] flex w-[min(30rem,100vw)] flex-col overflow-hidden border-grey30 border-l bg-white shadow-2xl"
        >
          {closeButton}
          {conversation}
        </div>
      )
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      aria-label="Get started"
      maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 720, height: '85vh', borderRadius: '12px' } }}
    >
      {closeButton}
      {conversation}
    </Dialog>
  );
}
